import { ApolloError, useMutation } from '@apollo/client';
import { Button, Icon, SettingsGroup, SettingsItemCheckbox, SettingsItemTextField } from '@elipssolution/harfang';
import { mdiPencil, mdiPlus } from '@mdi/js';
import { styled } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useConfirmationDialogContext } from '../../../../../../components/ConfirmationDialogProvider';
import SettingsDialogPage from '../../../../../../components/SettingsDialogPage';
import { useSettingsDialog } from '../../../../../../hooks/useSettingsDialog';
import { CustomerFileType } from '../../../../../../types/customerFile';
import { generateErrorInformations } from '../../../../../../utils/errorHandler';
import { SUCCESS_ANIMATION_DELAY } from '../../../../../../utils/statusAnimationDelay';
import {
	CREATE_SETTING_CUSTOMER_FILE_CHECK_ACCOUNT_TYPE,
	CreateSettingCustomerFileCheckAccountTypeType,
	REMOVE_SETTING_CUSTOMER_FILE_CHECK_ACCOUNT_TYPE,
	RemoveSettingCustomerFileCheckAccountTypeType,
	UPDATE_SETTING_CUSTOMER_FILE_CHECK_ACCOUNT_TYPE,
	UpdateSettingCustomerFileCheckAccountTypeType,
} from '../../../../api/settingCheckAccountType';
import { CheckTypeEnum } from '../../../../types/check';
import { SettingCustomerFileCheckAccountType } from '../../../../types/settingCheckAccountType';

const ActionWrapper = styled('div')({
	display: 'flex',
	flexDirection: 'row-reverse',
	justifyContent: 'space-between',
});

const ErrorWrapper = styled('div')(({ theme: { palette, shape } }) => ({
	height: 36,
	width: '50%',

	display: 'grid',
	placeItems: 'center',

	color: palette.error.main,
	backgroundColor: `${palette.error.main}1A`,
	borderRadius: shape.borderRadius,
}));

type CheckAccountTypesCustomerFileFormProps = {
	customerFileId?: CustomerFileType['id'];
	checkType: CheckTypeEnum;
	selectedCheckAccountType?: SettingCustomerFileCheckAccountType;
};

type FormFieldType = Pick<SettingCustomerFileCheckAccountType, 'accountPrefix' | 'isEnabled' | 'name'>;

const CheckAccountTypesCustomerFileForm = ({
	selectedCheckAccountType: initialCheckAccountType,
	checkType,
	customerFileId,
}: CheckAccountTypesCustomerFileFormProps) => {
	const { back, onHasDirtyFormChange } = useSettingsDialog();
	const { confirmAction } = useConfirmationDialogContext();

	const {
		control,
		formState: { isDirty, isValid },
		handleSubmit,
		reset,
	} = useForm<FormFieldType>();

	const [
		isCreateSettingCustomerFileCheckAccountTypeSucceeded,
		setIsCreateSettingCustomerFileCheckAccountTypeSucceeded,
	] = useState(false);
	const [
		isUpdateSettingCustomerFileCheckAccountTypeSucceeded,
		setIsUpdateSettingCustomerFileCheckAccountTypeSucceeded,
	] = useState(false);
	const [
		isOverloadSettingCustomerFileCheckAccountTypeSucceeded,
		setIsOverloadSettingCustomerFileCheckAccountTypeSucceeded,
	] = useState(false);
	const [checkAccountType, setCheckAccountType] = useState(initialCheckAccountType);
	const [errorMessage, setErrorMessage] = useState<string>();

	const {
		id: checkAccountTypeId,
		name: checkAccountTypeName,
		isInherited,
		isOverload,
	} = useMemo(() => (checkAccountType || {}) as SettingCustomerFileCheckAccountType, [checkAccountType]);

	const checkTypeMessage = useMemo(
		() => (checkType === CheckTypeEnum.CHECK_ISSUANCE ? `pour l'émission de chèques` : 'pour la remise de chèques'),
		[checkType],
	);

	const isDomainRelated = useMemo(() => isInherited || isOverload, [isInherited, isOverload]);

	const removeButtonName = useMemo(() => (isDomainRelated ? 'Revenir au domaine' : 'Supprimer'), [isDomainRelated]);

	const mutationButtonStartIcon = useMemo(
		() => <Icon path={checkAccountTypeId ? mdiPencil : mdiPlus} />,
		[checkAccountTypeId],
	);

	const [createSettingCustomerFileCheckAccountType, { loading: isCreateLoading }] =
		useMutation<CreateSettingCustomerFileCheckAccountTypeType>(CREATE_SETTING_CUSTOMER_FILE_CHECK_ACCOUNT_TYPE, {
			onCompleted: () => {
				if (checkAccountTypeId) {
					setIsOverloadSettingCustomerFileCheckAccountTypeSucceeded(true);
					setTimeout(() => setIsOverloadSettingCustomerFileCheckAccountTypeSucceeded(false), SUCCESS_ANIMATION_DELAY);
				} else {
					setIsCreateSettingCustomerFileCheckAccountTypeSucceeded(true);
					setTimeout(() => setIsCreateSettingCustomerFileCheckAccountTypeSucceeded(false), SUCCESS_ANIMATION_DELAY);
				}
			},
		});

	const [updateSettingCustomerFileCheckAccountType, { loading: isUpdateLoading }] =
		useMutation<UpdateSettingCustomerFileCheckAccountTypeType>(UPDATE_SETTING_CUSTOMER_FILE_CHECK_ACCOUNT_TYPE, {
			onCompleted: () => {
				setIsUpdateSettingCustomerFileCheckAccountTypeSucceeded(true);
				setTimeout(() => setIsUpdateSettingCustomerFileCheckAccountTypeSucceeded(false), SUCCESS_ANIMATION_DELAY);
			},
		});

	const [removeSettingCustomerFileCheckAccountType, { loading: isRemoveLoading }] =
		useMutation<RemoveSettingCustomerFileCheckAccountTypeType>(REMOVE_SETTING_CUSTOMER_FILE_CHECK_ACCOUNT_TYPE, {
			onCompleted: back,
			onError: (error: ApolloError) =>
				setErrorMessage(
					generateErrorInformations({
						error,
						resource: 'quickentry_removeSettingCustomerFileCheckAccountType',
					})?.message,
				),
		});

	const isLoading = useMemo(() => isCreateLoading || isUpdateLoading, [isCreateLoading, isUpdateLoading]);

	const isMutationButtonDisabled = useMemo(
		() => isRemoveLoading || !isValid || !isDirty,
		[isDirty, isRemoveLoading, isValid],
	);
	const mutationButtonName = useMemo(() => {
		if (checkAccountTypeId) {
			if (isInherited) return 'Surcharger';
			if (isOverloadSettingCustomerFileCheckAccountTypeSucceeded) return 'Type de compte surchargé';
			if (isCreateSettingCustomerFileCheckAccountTypeSucceeded) return 'Type de compte ajouté';
			if (isUpdateSettingCustomerFileCheckAccountTypeSucceeded) return 'Type de compte modifié';

			return 'Modifier';
		}

		return 'Ajouter';
	}, [
		checkAccountTypeId,
		isInherited,
		isOverloadSettingCustomerFileCheckAccountTypeSucceeded,
		isCreateSettingCustomerFileCheckAccountTypeSucceeded,
		isUpdateSettingCustomerFileCheckAccountTypeSucceeded,
	]);

	const onSubmit = useCallback(
		(values: FormFieldType) => {
			const { accountPrefix, isEnabled = true, name } = values;

			setErrorMessage('');

			const modeMutationInfoMap = new Map([
				[
					'create',
					{
						mutation: createSettingCustomerFileCheckAccountType,
						mutationName: 'quickentry_createSettingCustomerFileCheckAccountType',
						inputValues: {
							...(isInherited && { overriddenCheckAccountTypeId: checkAccountTypeId }),
							accountPrefix,
							customerFileId,
							isEnabled,
							name,
							checkType,
						},
					},
				],
				[
					'update',
					{
						mutation: updateSettingCustomerFileCheckAccountType,
						mutationName: 'quickentry_updateSettingCustomerFileCheckAccountType',
						inputValues: {
							id: checkAccountTypeId,
							isEnabled,
							name,
						},
					},
				],
			]);

			const {
				mutation,
				mutationName = '',
				inputValues,
			} = modeMutationInfoMap.get(!checkAccountTypeId || (isInherited && checkAccountTypeId) ? 'create' : 'update') ??
			{};

			return mutation?.({
				variables: {
					[`${mutationName}Input`]: inputValues,
				},
			})
				.then(({ data }) => {
					if (!data) return;

					const { id }: { id: string } = data[`${mutationName}` as keyof typeof data];

					setCheckAccountType({
						...values,
						isInherited: false,
						isOverload: isInherited ?? false,
						id,
					});
				})
				.catch((error: ApolloError) =>
					setErrorMessage(
						generateErrorInformations({
							error,
							resource:
								!checkAccountTypeId || (isInherited && checkAccountTypeId)
									? 'createSettingCustomerFileCheckAccountType'
									: 'updateSettingCustomerFileCheckAccountType',
						})?.message,
					),
				);
		},
		[
			createSettingCustomerFileCheckAccountType,
			isInherited,
			checkAccountTypeId,
			customerFileId,
			checkType,
			updateSettingCustomerFileCheckAccountType,
		],
	);

	const handleRemoveButtonClick = async () => {
		const isRemovalConfirmed = await confirmAction({
			title: `Êtes-vous sûr de vouloir ${
				isDomainRelated ? 'revenir au' : 'supprimer le'
			} type de compte de saisie chèques ${isDomainRelated ? 'domaine' : checkAccountTypeName} ?`,
			confirmButtonDetails: {
				color: 'error',
				label: removeButtonName,
			},
		});

		if (!isRemovalConfirmed) return null;

		return removeSettingCustomerFileCheckAccountType({
			variables: { quickentryRemoveSettingCustomerFileCheckAccountTypeId: checkAccountTypeId },
		});
	};

	useEffect(() => reset(checkAccountType), [reset, checkAccountType]);

	useEffect(() => {
		onHasDirtyFormChange(isDirty);
	}, [isDirty, onHasDirtyFormChange]);

	return (
		<SettingsDialogPage title={checkAccountTypeName ?? `Ajout d'un type de compte ${checkTypeMessage}.`}>
			<SettingsGroup>
				<Controller
					control={control}
					name="name"
					render={({ field }) => (
						<SettingsItemTextField
							{...field}
							description={`Nom du type de compte ${checkTypeMessage}.`}
							label="Nom"
							required
						/>
					)}
					rules={{ required: true }}
				/>
				<Controller
					control={control}
					name="accountPrefix"
					render={({ field }) => (
						<SettingsItemTextField
							{...field}
							description={`Préfixe du type de compte ${checkTypeMessage}.`}
							disabled={!!checkAccountTypeId}
							label="Préfixe de compte"
							required
						/>
					)}
					rules={{ required: true }}
				/>
				<Controller
					control={control}
					defaultValue
					name="isEnabled"
					render={({ field: { value, ...field } }) => (
						<SettingsItemCheckbox
							{...field}
							checked={value}
							description={`Si coché, le type du compte ${checkTypeMessage} est actif.`}
							label="Actif"
						/>
					)}
				/>
			</SettingsGroup>

			<ActionWrapper>
				<Button
					disabled={isMutationButtonDisabled}
					onClick={handleSubmit(onSubmit)}
					startIcon={mutationButtonStartIcon}
					variant="contained"
				>
					{mutationButtonName}
				</Button>

				{errorMessage && <ErrorWrapper>{errorMessage}</ErrorWrapper>}

				{checkAccountTypeId && !isInherited && (
					<Button color="error" disabled={isLoading} onClick={handleRemoveButtonClick} variant="outlined">
						{removeButtonName}
					</Button>
				)}
			</ActionWrapper>
		</SettingsDialogPage>
	);
};

export default CheckAccountTypesCustomerFileForm;
